import _ from 'lodash';
import { type Board } from '@gorilla/common/src/lib/monday-api/api';
import { type Theme } from '@gorilla/portal-shared/src/schemas';
import { Box, Icon } from '@vibe/core';
import { Person, DropdownChevronRight, Activity } from '@vibe/icons';
import { Logo } from './Logo';

type ThemePreviewProps = {
  themeSettings: Theme;
  clientBoard: Board;
  screenMode: 'small' | 'medium' | 'large';
};

export function ThemePreview({ themeSettings, clientBoard, screenMode }: ThemePreviewProps) {
  return (
    <Box
      shadow="small"
      rounded="small"
      style={{
        width: '100%',
        maxWidth: '306px',
        minWidth: '306px',
        height: '150px',
      }}
    >
      <div
        style={{
          pointerEvents: 'none',
          userSelect: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          height: 'inherit',
        }}
      >
        <div
          style={{
            marginLeft: `${themeSettings.sidebarCollapsedByDefault ? '-70px' : '0'}`,
            width: '80px',
            minWidth: '80px',
            height: 'inherit',
            backgroundColor: `${themeSettings.primaryColor}`,
          }}
        >
          <div
            style={{
              position: 'relative',
              padding: '10px 15px',
              width: 'calc(100% - 30px)',
              opacity: 1,
            }}
          >
            <div
              style={{
                fontSize: '7px',
                fontWeight: 600,
                lineHeight: '12px',
                whiteSpace: 'nowrap',
                color: `${themeSettings.secondaryColor}`,
              }}
            >
              Client Portal
            </div>
            {themeSettings.logo && clientBoard ? (
              <div
                style={{
                  position: 'absolute',
                  top: '6px',
                  left: '15px',
                  width: '55px',
                  maxWidth: 'calc(100% - 30px)',
                  height: '20px',
                  backgroundColor: `${themeSettings.primaryColor}`,
                }}
              >
                <Logo fileHash={themeSettings.logo} boardId={clientBoard.id} />
              </div>
            ) : null}
          </div>

          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: `${themeSettings.secondaryColor}`,
              opacity: 0.15,
            }}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '5px',
              padding: '15px',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.secondaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.secondaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.secondaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.secondaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.secondaryColor}`,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '17px',
              padding: '10px 15px',
              opacity: 1,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '100%',
                backgroundColor: `${themeSettings.secondaryColor}`,
                color: `${themeSettings.primaryColor}`,
              }}
            >
              <Icon icon={Person} size={8} />
            </div>
            <div
              style={{
                fontSize: '4px',
                fontWeight: 600,
                lineHeight: '6px',
                textAlign: 'left',
                color: `${themeSettings.secondaryColor}`,
              }}
            >
              <div
                style={{
                  marginBottom: '3px',
                  width: '10px',
                  height: '2px',
                  borderRadius: '2px',
                  backgroundColor: `${themeSettings.secondaryColor}`,
                }}
              ></div>
              <div
                style={{
                  width: '20px',
                  height: '2px',
                  borderRadius: '2px',
                  backgroundColor: `${themeSettings.secondaryColor}`,
                }}
              ></div>
            </div>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            width: '100%',
            height: 'inherit',
            backgroundColor: `${themeSettings.secondaryColor}`,
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '8px 8px 8px 10px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '18px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '7px',
                fontWeight: 'bold',
                lineHeight: '1px',
                color: `${themeSettings.teritaryColor}`,
              }}
            >
              <div
                style={{
                  width: '15px',
                  height: '3px',
                  borderRadius: '2px',
                  backgroundColor: `${themeSettings.teritaryColor}`,
                }}
              ></div>
              &nbsp;
              <Icon icon={DropdownChevronRight} size={8} />
              &nbsp;
              <div
                style={{
                  width: '15px',
                  height: '3px',
                  borderRadius: '2px',
                  backgroundColor: `${themeSettings.teritaryColor}`,
                }}
              ></div>
            </div>
            <div
              style={{
                display: 'inline-block',
                margin: '5px 5px 5px 0',
                width: '25px',
                height: '5px',
                borderRadius: '8px',
                border: `1px solid ${themeSettings.primaryColor}`,
                backgroundColor: `${themeSettings.primaryColor}`,
                fontSize: '3px',
                fontWeight: 600,
                lineHeight: '13px',
                textAlign: 'center',
                color: `${themeSettings.secondaryColor}`,
              }}
            >
              &nbsp;
            </div>
          </div>
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: `${themeSettings.teritaryColor}`,
              opacity: 0.15,
            }}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '5px',
              margin: '20px',
            }}
          >
            <div
              style={{
                marginBottom: '4px',
                width: '60px',
                height: '8px',
                borderRadius: '8px',
                backgroundColor: `${themeSettings.teritaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.teritaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.teritaryColor}`,
              }}
            />
            <div
              style={{
                width: '100%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.teritaryColor}`,
              }}
            />
            <div
              style={{
                width: '50%',
                height: '2px',
                borderRadius: '2px',
                backgroundColor: `${themeSettings.teritaryColor}`,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '5px',
              margin: '20px',
              borderRadius: '4px',
              backgroundColor: `${themeSettings.specialColor}`,
            }}
          >
            <div
              style={{
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '5px',
                  margin: '15px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
                <div
                  style={{
                    width: '100%',
                    height: '2px',
                    borderRadius: '2px',
                    backgroundColor: `${themeSettings.teritaryColor}`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
}
